import React, { useMemo } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

// ✅ keep only filters + category_name
const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
];

const buildSafeSearch = (searchParams) => {
  const next = new URLSearchParams();
  keepKeys.forEach((k) => {
    const v = searchParams.get(k);
    if (v) next.set(k, v);
  });
  return next;
};

const Blog = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ navbar se aane wala category_name (code)
  const categoryName = searchParams.get("category_name") || "";
  const sectionFromQuery = searchParams.get("section"); // ✅ persistent
  const sectionFromState = location.state?.fromSection; // ✅ only if no refresh
  const sectionKey = sectionFromState || sectionFromQuery;

  const {
    item: blogs,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: blogsPending,
  } = useItemCategoryInfinite({
    key: "connect_blog",
    endpoint: "blogs",
    listKey: "blogs",
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
    featured: sectionKey === "features" ? 1 : undefined,
  });

  const { data: apiblogsData = [] } = useQuery({
    queryKey: [
      "apiblogsData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);
      params.append("is_featured", 1);
      const res = await axios.get(
        `${saffronURL}/api/v1/public/get-feature-blogs?${params.toString()}`,
      );
      return res.data;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const hasBlogs = blogs?.length > 0;
  const getBlogs = useMemo(() => addPosition(sectionKey === "features" ? apiblogsData?.data : blogs), [apiblogsData?.data, blogs, sectionKey]);

  // Menu data
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60, // ✅ v5
    refetchOnWindowFocus: false,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "blog");
  }, [menuData]);

  // ✅ category change => URL update => list refetch
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  // ✅ CARD CLICK => NAVIGATE TO DETAILS (Prev/Next will work there)
  const handleOpenDetails = (item) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    const next = buildSafeSearch(searchParams);
    next.set("section", "blogsList"); // ✅ ConnectDetailsPage uses this to build list

    navigate(
      {
        pathname: `${saffronRout}/details/blogs/${slug}`,
        search: `?${next.toString()}`,
      },
      {
        state: {
          sectionKey: "blogsList",
          position: item?.position ?? 0,
          label: item?.title || "Blog",
        },
      },
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />

      <section className="py-3 px-1 md:px-5 lg:px-10">
        {/* List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getBlogs?.map((item) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleOpenDetails(item)}
              imgH=" h-[200px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {/* Status */}
        {blogsPending && <p className="text-gray-500">Loading blogs…</p>}

        {!blogsPending && !hasBlogs && (
          <p className="text-gray-400 italic">
            No articles found in <span className="font-medium">'Blogs'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Blog;
